import { useEffect, useState } from 'react';
import { findOrCreateIngredient, listIngredients } from '../../lib/ingredients';
import { PHASES } from '../../lib/formulaModel';
import PhaseKey from './PhaseKey';
import { formatPct, sumPct } from '../../lib/lab';
import { formatJMD } from '../../lib/finances';

const BLANK_LINE = { ingredient_name: '', ingredient_id: null, phase: '', percentage: '', notes: '' };

/* The formula editor.
 *
 * A formula is percentages, and nothing else. Batch size, expected yield and
 * the fill calculator all left this form: scaling a batch should never mean
 * editing the formula, which is exactly what the old fill calculator made
 * you do by sitting inside it. They live in Make a batch now.
 */
export default function FormulaEditor({
  formula,
  version,
  productTypes,
  requireChangeNote,
  hasVariants,
  onSubmit,
  onCancel,
}) {
  const [library, setLibrary] = useState([]);
  const [name, setName] = useState(formula?.name || '');
  const [productTypeId, setProductTypeId] = useState(
    formula?.product_type_id || productTypes?.[0]?.id || ''
  );
  const [changeNote, setChangeNote] = useState('');
  const [notes, setNotes] = useState(version?.notes || '');
  const [flexKey, setFlexKey] = useState(null);
  const [lines, setLines] = useState(() => {
    const existing = version?.formula_ingredients || [];
    return existing.length
      ? existing.map((l) => ({
          ingredient_name: l.ingredient_name || '',
          ingredient_id: l.ingredient_id || null,
          phase: l.phase || '',
          percentage: l.percentage ?? '',
          notes: l.notes || '',
        }))
      : [{ ...BLANK_LINE }];
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    listIngredients()
      .then(setLibrary)
      .catch(() => setLibrary([]));
  }, []);

  // The flex line is tracked by row index, because a brand-new line has no
  // ingredient id until it's saved.
  useEffect(() => {
    if (flexKey !== null || !version?.flex_ingredient_id) return;
    const index = (version.formula_ingredients || []).findIndex(
      (l) => l.ingredient_id === version.flex_ingredient_id
    );
    if (index >= 0) setFlexKey(index);
  }, [version, flexKey]);

  const update = (i, field, value) =>
    setLines((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const updateName = (i, value) => {
    const match = library.find((l) => l.name.toLowerCase() === value.trim().toLowerCase());
    setLines((rows) =>
      rows.map((r, idx) =>
        idx === i ? { ...r, ingredient_name: value, ingredient_id: match?.id || null } : r
      )
    );
  };

  const filled = lines.filter((l) => l.ingredient_name.trim());

  /* Balance the base with the flex line.
   *
   * The flex line resolves variants automatically, but the base formula is
   * something you're defining by hand — so nothing changes under you as you
   * type. This is the same arithmetic offered as one click, because doing it
   * on paper is where a formula quietly stops adding up to 100%.
   */
  const balanceWithFlex = () => {
    if (flexKey === null) return;
    const others = lines.reduce(
      (sum, l, i) => (i === flexKey || !l.ingredient_name.trim() ? sum : sum + (Number(l.percentage) || 0)),
      0
    );
    const remainder = Math.round((100 - others) * 10000) / 10000;
    if (remainder < 0) return;
    setLines((rows) => rows.map((r, i) => (i === flexKey ? { ...r, percentage: remainder } : r)));
  };
  const total = sumPct(filled.map((l) => ({ percentage: Number(l.percentage) || 0 })));
  const balanced = Math.abs(total - 100) < 0.005;

  // Cost is shown only when it means something. The old build printed
  // "Cost unavailable" on every card while 8 of 8 ingredients were unpriced
  // — a nag, not information.
  const priced = filled.filter((l) => {
    const found = library.find((i) => i.id === l.ingredient_id);
    return Number(found?.cost_per_kg) > 0;
  });
  const unpricedCount = filled.length - priced.length;
  const batchCost = priced.reduce((sum, l) => {
    const found = library.find((i) => i.id === l.ingredient_id);
    return sum + (Number(found.cost_per_kg) * (Number(l.percentage) || 0)) / 100 / 1000;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!filled.length) {
      setError('Add at least one ingredient.');
      return;
    }
    if (!balanced) {
      setError(`The percentages total ${formatPct(total)}. A formula has to come to 100%.`);
      return;
    }
    if (requireChangeNote && !changeNote.trim()) {
      setError('Describe what changed — the note is what makes version history useful later.');
      return;
    }
    if (hasVariants && flexKey === null) {
      setError(
        'This formula has variants, so one line has to be the flex ingredient that absorbs their differences.'
      );
      return;
    }

    setSubmitting(true);
    try {
      // Every line is linked to the library on save. Overrides are keyed by
      // ingredient id, so a line left as loose text could never be varied.
      const resolved = [];
      for (const line of filled) {
        let id = line.ingredient_id;
        if (!id) {
          const created = await findOrCreateIngredient(line.ingredient_name.trim(), library);
          id = created?.id || null;
        }
        resolved.push({
          ingredient_name: line.ingredient_name.trim(),
          ingredient_id: id,
          phase: line.phase || null,
          percentage: Number(line.percentage) || 0,
          notes: line.notes || null,
        });
      }

      // flexKey indexes `lines`; `resolved` is built from `filled`, which
      // skips blank rows. Same object references, so indexOf lines them up.
      const flexRow = flexKey === null ? null : lines[flexKey];
      const flexIndex = flexRow ? filled.indexOf(flexRow) : -1;
      const flexId = flexIndex >= 0 ? resolved[flexIndex].ingredient_id : null;

      await onSubmit(
        {
          product_name: name.trim(),
          product_type: productTypes.find((t) => t.id === productTypeId)?.name || 'Other',
          version_label: version ? `v${(version.version_number || 0) + 1}` : 'v1',
          notes: notes || null,
          flex_ingredient_id: flexId,
        },
        resolved,
        changeNote.trim() || null,
        { product_type_id: productTypeId }
      );
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <form className="formula-form lab-editor" onSubmit={handleSubmit}>
      {requireChangeNote && (
        <label className="formula-change-note">
          What changed in this version?
          <input
            type="text"
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            placeholder="e.g. Raised shea butter to 14%, dropped fragrance to 0.8%"
          />
          <span className="formula-change-hint">
            Saving creates a new version. The current one stays intact, and any batch already made
            from it keeps referencing exactly what it used.
          </span>
        </label>
      )}

      <div className="lab-form-grid">
        <label className="lab-form-wide">
          Formula name
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label>
          Product type
          <select value={productTypeId} onChange={(e) => setProductTypeId(e.target.value)}>
            {(productTypes || []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <datalist id="ingredient-library">
        {library.map((l) => (
          <option key={l.id} value={l.name} />
        ))}
      </datalist>

      <div className="lab-lines">
        <div className="lab-line lab-line-head">
          <span>Ingredient</span>
          <span>Phase</span>
          <span>%</span>
          <span>Note</span>
          <span title="The line that absorbs variant differences">Flex</span>
          <span />
        </div>

        {lines.map((line, i) => (
          <div className="lab-line" key={i}>
            <input
              type="text"
              list="ingredient-library"
              value={line.ingredient_name}
              onChange={(e) => updateName(i, e.target.value)}
              placeholder="e.g. Shea Butter"
            />
            <select value={line.phase || ''} onChange={(e) => update(i, 'phase', e.target.value)}>
              <option value="">Unassigned</option>
              {PHASES.filter((p) => p !== 'Unassigned').map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {/* step="any": actives and preservatives are dosed at fractions
                like 0.025%, which a fixed step refuses. */}
            <input
              type="number"
              min="0"
              max="100"
              step="any"
              value={line.percentage}
              onChange={(e) => update(i, 'percentage', e.target.value)}
            />
            <input
              type="text"
              value={line.notes || ''}
              onChange={(e) => update(i, 'notes', e.target.value)}
              placeholder="optional"
            />
            <label className="lab-flex-radio">
              <input
                type="radio"
                name="flex-ingredient"
                checked={flexKey === i}
                onChange={() => setFlexKey(i)}
                aria-label={`Use ${line.ingredient_name || 'this line'} as the flex ingredient`}
              />
            </label>
            <button
              type="button"
              className="formula-ingredient-remove"
              onClick={() => {
                setLines((rows) => rows.filter((_, idx) => idx !== i));
                setFlexKey((k) => (k === i ? null : k !== null && k > i ? k - 1 : k));
              }}
              disabled={lines.length === 1}
            >
              Remove
            </button>
          </div>
        ))}

        <button
          type="button"
          className="yield-add"
          onClick={() => setLines((rows) => [...rows, { ...BLANK_LINE }])}
        >
          + Add ingredient
        </button>

        <PhaseKey />
      </div>

      {flexKey !== null && (
        <p className="batch-form-hint">
          {lines[flexKey]?.ingredient_name || 'This line'} will absorb the difference when a variant
          changes another line, so every variant still totals 100%.
        </p>
      )}

      <label className="lab-form-wide">
        Notes
        <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      {error && <p className="formula-error">{error}</p>}

      {/* Sticky, because the total is the thing that decides whether you can
          save, and it was previously off-screen below the ingredient rows. */}
      <div className="lab-editor-footer">
        <div className={'lab-editor-total' + (balanced ? ' ok' : ' off')}>
          <span>Total</span>
          <strong>{formatPct(total)}</strong>
          {!balanced && (
            <em>{total > 100 ? `${formatPct(total - 100)} over` : `${formatPct(100 - total)} short`}</em>
          )}
        </div>

        {!balanced && flexKey !== null && lines[flexKey]?.ingredient_name.trim() && (
          <button type="button" className="ghost-button" onClick={balanceWithFlex}>
            Balance with {lines[flexKey].ingredient_name}
          </button>
        )}

        {priced.length > 0 && (
          <div className="lab-editor-cost">
            Batch cost: {formatJMD(batchCost * 1000)} per kg
            {unpricedCount > 0 && (
              <em>
                {' '}
                ({unpricedCount} of {filled.length} ingredients unpriced)
              </em>
            )}
          </div>
        )}

        <div className="ingredient-form-actions">
          <button type="button" className="ghost-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={submitting || !balanced}>
            {submitting ? 'Saving…' : version ? 'Save as new version' : 'Create formula'}
          </button>
        </div>
      </div>
    </form>
  );
}
